import React from 'react';
import { am5, am5themes_Moonrise, am5xy, mui } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { createAxes } from './methods/create-axes';
import { createSeries } from './methods/create-series';
import { addLabels } from '../shared/add-labels';
import { createBaseTooltips } from '../shared/create-base-tooltips';
import { createDownloadButton } from '../shared/create-download-button';
import { handleZoom } from '../shared/handle-zoom';
import { getPaddingLeft } from '../shared/helpers';
import { useChartOnReady } from '../shared/use-chart-on-ready';

export type ChartRef = {
  series: any[] | null;
  seriesKeys: string[];
  onReady: (_callback: (_series: any[]) => void) => void;
};

export type ChartProps = {
  id: string;
  data: types.widgets.TableData;
  fullScreen: boolean;
  numberFormat: string | null;
  download: boolean;
  tooltipText: string;
  yAxisLabel: string;
  xAxisLabel: string;
  category: string;

  rotation: number;
  seriesOrder: (_c: string[]) => string[];
  seriesColorByName: Record<string, string>;
  displayNumbers: boolean;
  stacked: boolean;
  negativeColor: string;
  positiveColor: string;
  horizontal: boolean;
  colorByBar: boolean;
  count: types.widgets.CountData | undefined;
  minAxisValue: number | undefined;
  hideFromChart: string[];
};

const Chart = React.forwardRef<ChartRef, ChartProps>(
  (
    {
      id,
      data,
      count,
      fullScreen,
      numberFormat,
      download,
      yAxisLabel,
      xAxisLabel,
      category,
      tooltipText,
      rotation,
      seriesOrder,
      seriesColorByName,
      displayNumbers,
      stacked,
      negativeColor,
      positiveColor,
      horizontal,
      colorByBar,
      minAxisValue,
      hideFromChart,
    },
    ref
  ) => {
    const widgetId = `chart-${id}-${fullScreen ? 'fs' : 'sw'}`;
    const theme = mui.styles.useTheme() as mui.core.Theme;
    const seriesRef = React.useRef<any>(null);
    const seriesKeys = Object.keys(data?.[0] ?? {}).filter((key) => key !== category);
    const [seriesState, setSeriesState] = React.useState<any[] | null>(null);
    const { registerCallback, executeCallbacks } = useChartOnReady();

    React.useImperativeHandle(
      ref,
      () => ({
        series: seriesState,
        seriesKeys,
        onReady: registerCallback,
      }),
      [seriesState, seriesKeys, registerCallback]
    );

    React.useEffect(() => {
      // Create root element
      const root = am5.Root.new(widgetId);

      // Remove amCharts logo
      root._logo?.dispose();

      // Set themes
      root.setThemes([am5themes_Moonrise.new(root)]);

      // Create chart
      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          wheelX: 'panX',
          wheelY: 'zoomX',
          pinchZoomX: true,
          layout: root.verticalLayout,
          maxTooltipDistance: 0,
          paddingLeft: getPaddingLeft(download, yAxisLabel),
        })
      );

      // Root settings
      chart.appear(1000, 100);
      if (numberFormat) {
        root.numberFormatter.setAll({
          numberFormat,
          numericFields: ['valueY'],
        });
      }

      // Create elements
      const { xAxis, yAxis } = createAxes({ root, chart, data, category, horizontal, minAxisValue });

      const barAxis = horizontal ? xAxis : yAxis;
      const extraAmount = horizontal ? 0.05 : 0.15;
      barAxis.setAll({
        extraMax: extraAmount,
        extraMin: minAxisValue !== undefined ? 0 : extraAmount,
      });

      const createdSeries = createSeries({
        root,
        chart,
        data,
        category,
        xAxisRenderer: xAxis as any,
        yAxisRenderer: yAxis as any,
        tooltipText,
        seriesOrder,
        seriesColorByName,
        rotation,
        displayNumbers,
        stacked,
        negativeColor,
        positiveColor,
        horizontal,
        colorByBar,
        count,
        hideFromChart,
        hideGridLines: true,
      });

      seriesRef.current = createdSeries;
      setSeriesState(createdSeries);

      createBaseTooltips({
        root,
        chart,
        xAxis: xAxis as any,
        yAxis: yAxis as any,
        theme,
        zoomType: horizontal ? 'zoomY' : 'zoomX',
      });
      handleZoom(chart);
      if (download) createDownloadButton(root, category, data);
      addLabels({ root, yAxis, xAxis, yAxisLabel, xAxisLabel });

      const cursor = chart.set(
        'cursor',
        am5xy.XYCursor.new(root, {
          behavior: 'none', // or "zoomX", "zoomY", etc., depending on your use case
        })
      );

      // Hide the vertical and horizontal reference lines
      cursor.lineX.set('visible', false);
      cursor.lineY.set('visible', false);
      // Disable the tooltips on the axes
      (xAxis as any).set('tooltip', null);
      (yAxis as any).set('tooltip', null);

      // Execute callbacks when chart has actually finished rendering and animating
      executeCallbacks(createdSeries);

      return () => {
        setSeriesState(null);
        root.dispose();
      };
    }, [data, seriesColorByName]);

    return (
      <mui.core.Box
        sx={{
          width: '100%',
          height: '100%',
        }}
      >
        <div
          id={widgetId}
          style={{
            width: '100%',
            height: '100%',
          }}
        />
      </mui.core.Box>
    );
  }
);

export default Chart;
